'use client';
/**
 * The thin bar at the top of the viewport while a route change is in flight.
 *
 * On a phone over a slow link — or in `next dev`, where a route is compiled the first time it is
 * opened — a tap on a plain link used to change nothing on screen for seconds. It read as an ignored
 * tap, and people tapped again (docs/10-ux-audit.md · UX-45). Every `<Link>` and `useRouter()` in
 * `@/i18n/navigation` reports to one counter; this draws it. It is kept off screen for the first
 * 150 ms (`k-nav-progress`) so an instant, prefetched navigation never flashes it.
 *
 * The bar's visibility is written to the DOM by the store subscription, not rendered from state.
 * React does not always flush a state update made while a navigation is pending when that update
 * comes from outside a React event: on the editor's history-guarded exit the counter went up, but
 * a render-driven bar stayed absent until some unrelated click flushed it. Toggling `hidden`
 * directly has no such dependency.
 */
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { isNavigationPending, subscribeNavigation } from '@/lib/navigation-status';

export function NavigationProgress() {
  const t = useTranslations('common');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const sync = () => {
      if (ref.current) ref.current.hidden = !isNavigationPending();
    };
    sync();
    return subscribeNavigation(sync);
  }, []);

  return (
    <div
      ref={ref}
      hidden
      role="progressbar"
      aria-label={t('loading')}
      aria-busy="true"
      className="k-nav-progress pointer-events-none fixed inset-x-0 top-0 z-(--z-toast) h-[3px] overflow-hidden bg-accent/25"
    >
      <span className="k-nav-progress-runner absolute inset-y-0 w-2/5 rounded-full bg-accent motion-reduce:inset-x-0 motion-reduce:w-auto motion-reduce:animate-none" />
    </div>
  );
}
